import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Checkbox,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Grid,
  Tooltip,
  Typography,
  TextField,
  MenuItem,
  Select,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { LabelWithControl } from "components/common/LabelControl";
import { Link, useNavigate } from "react-router-dom";
import ReasonCodesPopup from "components/Validate/ReasonCodesPopup";
import ReactDOM from "react-dom/client";
import { useDispatch, useSelector } from "react-redux";
import { approvalManualOrders } from "redux/actions/approvalManualOrderAction";
import { SOMS_API_KEY } from "services/CONSTANTS";
import { processOrders } from "redux/actions/processOrdersActions";
import {
  RESET_PROCESS_ORDERS,
  RESET_APPROVAL_ORDER,
  UPDATE_APPROVAL_ORDER,
} from "redux/actions/action-type";
import { Provider } from "react-redux";
import { store as reduxStore, persistor } from "redux/store";
import { PersistGate } from "redux-persist/integration/react";
import { showNotificationDialogManualOrder } from "redux/actions/notificationManualOrderAction";
import {
  PAGE,
  ORDER_ACTIONS,
  CUTOFF_ACTION,
} from "components/common/CONSTANTS";
import CutOffTimeAlert from "components/common/cutOffTimeAlert";

export const ApproveManualOrders = () => {
  const [orderDateFrom, setOrderDateFrom] = useState(dayjs());
  const [orderDateTo, setOrderDateTo] = useState(dayjs().add(4, "day"));
  const [selectedRows, setSelectedRows] = useState([]);
  const [pin, setPin] = useState("");
  const [min, setMin] = useState("");
  const [depot, setDepot] = useState("");
  const [store, setStore] = useState("");
  const [reason, setReason] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [mandatoryFieldsError, setMandatoryFieldsError] = useState([]);
  const [reasonCodes, setReasonCodes] = useState();
  const [offset, setOffset] = useState(0);
  const [approvedOrders, setApprovedOrders] = useState();
  const [isBulkApprove, setIsBulkApprove] = useState(false);
  const [isFetchingAllOrders, setIsFetchingAllOrders] = useState(false);
  const isMounted = useRef();
  const tableContainerRef = useRef();
  const isProcessOrder = useRef();

  const limit = 100;
  const ORDER_STATUS = "pending";

  const userType = useSelector((state) => state.user);
  const { locationResponse } = useSelector((state) => state.locations);
  const { accessiblePages } = userType.userType;
  const [validStores, setValidStores] = useState([]);

  const { orders, metadata, isviewManualOrdersLoading, errorDetails } =
    useSelector((state) => state.approval);
  const { isProcessOrderSuccess, processOrderResponse } = useSelector(
    (state) => state.processOrders
  );
  const { reasonCodesResponse } = useSelector((state) => state.reasonCodes);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchOrders = useCallback(
    (storesToSearch = [], updatedOffset) => {
      const pinArray = pin
        ? pin.split(",").map((num) => Number(num.trim()))
        : [];
      const minArray = min
        ? min.split(",").map((num) => Number(num.trim()))
        : [];
      const depotArray = depot
        ? depot.split(",").map((num) => Number(num.trim()))
        : [];
      const storeArray =
        storesToSearch.length > 0 ? storesToSearch : validStores;

      //Validations
      let hasFieldErrors = false;
      if (pin && !isNumericWithCommas(pin)) {
        updateValidationErrors(
          "pin",
          "PIN should contain only numeric values."
        );
        hasFieldErrors = true;
      }
      if (pinArray.length > 50) {
        updateValidationErrors("pin", "PIN can have a maximum of 50 entries.");
        hasFieldErrors = true;
      }
      if (min && !isNumericWithCommas(min)) {
        updateValidationErrors(
          "min",
          "MIN should contain only numeric values."
        );
        hasFieldErrors = true;
      }
      if (minArray.length > 50) {
        updateValidationErrors("min", "MIN can have a maximum of 50 entries.");
        hasFieldErrors = true;
      }
      if (depot && !isNumericWithCommas(depot)) {
        updateValidationErrors(
          "depot",
          "Depot should contain only numeric values."
        );
        hasFieldErrors = true;
      }
      if (depotArray.length > 50) {
        updateValidationErrors(
          "depot",
          "Depot can have a maximum of 50 entries."
        );
        hasFieldErrors = true;
      }
      if (store && !isNumericWithCommas(store)) {
        updateValidationErrors(
          "store",
          "Store should contain only numeric values."
        );
        hasFieldErrors = true;
      }
      if (orderDateFrom && !dayjs(orderDateFrom).isValid()) {
        updateValidationErrors(
          "orderDateFrom",
          "Please enter date in correct format"
        );
        hasFieldErrors = true;
      }
      if (orderDateTo && !dayjs(orderDateTo).isValid()) {
        updateValidationErrors(
          "orderDateTo",
          "Please enter date in correct format"
        );
        hasFieldErrors = true;
      }
      if (orderDateFrom && orderDateFrom.isBefore(dayjs(), "day")) {
        updateValidationErrors(
          "orderDateFrom",
          "Please enter current or future date"
        );
        hasFieldErrors = true;
      }
      if (orderDateTo && orderDateTo.isBefore(dayjs(), "day")) {
        updateValidationErrors(
          "orderDateTo",
          "Please enter current or future date"
        );
        hasFieldErrors = true;
      }

      if (!hasFieldErrors) {
        const reqBody = {
          orderDateFrom: dayjs(orderDateFrom).format("YYYY-MM-DD"),
          orderDateTo: dayjs(orderDateTo).format("YYYY-MM-DD"),
          packId: pinArray,
          itemId: minArray,
          shipFromLocationId: depotArray,
          shipToLocationId: storeArray,
          orderStatus: ORDER_STATUS,
          reasonCode: reason,
          userType: "CENTRAL_SUPPLYCHAIN",
        };

        dispatch(
          approvalManualOrders({
            apiKey: SOMS_API_KEY,
            limit: limit,
            offset: updatedOffset !== undefined ? updatedOffset : offset,
            reqBody: reqBody,
          })
        );
      }
    },
    [
      store,
      depot,
      dispatch,
      min,
      offset,
      orderDateFrom,
      orderDateTo,
      pin,
      reason,
      validStores,
    ]
  );

  const handleInfiniteScroll = useCallback(async () => {
    try {
      const { scrollTop, scrollHeight, clientHeight } =
        tableContainerRef.current;
      if (
        scrollTop + clientHeight >= scrollHeight - 1 &&
        !isviewManualOrdersLoading
      ) {
        setOffset(orders.length);
      }
    } catch (error) {
      console.log(error, "error");
    }
  }, [orders, isviewManualOrdersLoading, tableContainerRef]);

  useEffect(() => {
    if (isMounted.current) return;

    isMounted.current = true;
    isProcessOrder.current = false;
    if (
      processOrderResponse &&
      Object.hasOwn(processOrderResponse, "metadata")
    ) {
      isProcessOrder.current = true;
    }
    dispatch({ type: RESET_PROCESS_ORDERS });
    if (accessiblePages) {
      let channelList = accessiblePages[PAGE.APPROVE_MANUAL_ORDERS.key];
      const stores = [];
      locationResponse.forEach(function (location) {
        if (channelList.indexOf(location.channel.toLowerCase()) > -1) {
          stores.push(location.locationId);
        }
      });
      setValidStores(stores);
      if (stores.length > 0) {
        fetchOrders();
      }
    }

    return () => {
      dispatch({ type: RESET_APPROVAL_ORDER });
    };
  }, [accessiblePages, locationResponse]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    tableContainer.addEventListener("scroll", handleInfiniteScroll);
    return () =>
      tableContainer.removeEventListener("scroll", handleInfiniteScroll);
  }, [isviewManualOrdersLoading, handleInfiniteScroll]);

  //Disabled eslint check. Adding missing dependency causes rerenders.
  useEffect(() => {
    if (metadata && metadata.totalCount > offset && !isFetchingAllOrders) {
      fetchOrders();
    }
  }, [offset]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (reasonCodesResponse) {
      const { reasonCodes: allReasonCodes } = reasonCodesResponse;
      const filteredReasonCodes = allReasonCodes.filter(
        (reason) => reason.orderType === "Manual Orders"
      );
      const uniqueReasonCodes = Array.from(
        filteredReasonCodes
          .reduce((map, item) => {
            if (!map.has(item.reasonCode)) {
              map.set(item.reasonCode, item);
            }
            return map;
          }, new Map())
          .values()
      );
      setReasonCodes(uniqueReasonCodes);
    }
  }, [reasonCodesResponse, setReasonCodes]);

  useEffect(() => {
    if (isProcessOrderSuccess) {
      const { metadata, orders: processedOrders } = processOrderResponse;
      const { validationfailed, totalrecordsProcessed } = metadata;
      let title;
      let text;
      if (validationfailed > 0 && isProcessOrder.current === false) {
        const receievdErrors = processedOrders[0].errors;
        title = "Error in approval";
        text = `${totalrecordsProcessed} order processed. `;
        text = text + `Validation failed in ${validationfailed} records. `;
        text = text + `${receievdErrors[0].message}`;

        const multipleErrorsMsg = (
          <>
            <Typography variant="span">{text}</Typography>
          </>
        );
        dispatch(
          showNotificationDialogManualOrder(title, multipleErrorsMsg, "Done")
        );
      }
      if (validationfailed === 0) {
        title = "Manual orders approved successfully.";
        text = `${totalrecordsProcessed} order processed.`;

        const multipleErrorsMsg = (
          <>
            <Typography variant="span">{text}</Typography>
          </>
        );
        dispatch(
          showNotificationDialogManualOrder(title, multipleErrorsMsg, "Done")
        );
      }

      if (isBulkApprove) {
        dispatch({ type: RESET_APPROVAL_ORDER });
        setIsBulkApprove(false);
        const updatedOffset = 0;
        setOffset(updatedOffset);
        fetchOrders(updatedOffset);
      }

      if (!isBulkApprove && (!validationfailed || validationfailed < 1)) {
        const updatedData = orders.filter(
          (order) =>
            !approvedOrders.some(
              (approvedOrder) =>
                approvedOrder.manualOrderId === order.manualOrderId
            )
        );

        dispatch({
          type: UPDATE_APPROVAL_ORDER,
          payload: { orders: updatedData },
        });
      }
      setSelectedRows([]);
      setPin("");
      setMin("");
      dispatch({ type: RESET_PROCESS_ORDERS });
    }
  }, [
    isProcessOrderSuccess,
    dispatch,
    selectedRows.length,
    processOrderResponse,
    fetchOrders,
    approvedOrders,
    orders,
    isBulkApprove,
    isProcessOrder,
  ]);

  useEffect(() => {
    if (orders.length > 0) {
      if (isBulkApprove) {
        const pendingOrders = orders.map((o) => o.manualOrderId);
        setSelectedRows(pendingOrders);
      }
    }
  }, [orders.length]); //eslint-disable-line react-hooks/exhaustive-deps

  const handleCheckboxChange = (manualOrderId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(manualOrderId)
        ? prevSelectedRows.filter((row) => row !== manualOrderId)
        : [...prevSelectedRows, manualOrderId]
    );
  };

  const handleSelectAll = async () => {
    if (selectedRows.length > 0 && selectedRows.length === orders.length) {
      setSelectedRows([]);
      setIsBulkApprove(false);
    } else {
      setIsFetchingAllOrders(true);
      const pendingOrders = orders.map((o) => o.manualOrderId);
      setSelectedRows(pendingOrders);
      setIsBulkApprove(true);

      if (metadata && metadata.totalCount > orders.length) {
        let offset = orders.length;
        const validSearchStores = [];

        if (store) {
          const storeArr = store.split(",").map((x) => parseInt(x));
          storeArr.forEach(function (store) {
            if (validStores.indexOf(store) > 0) {
              validSearchStores.push(store);
            }
          });
        }

        // Keep fetching until we have all orders
        while (offset < metadata.totalCount) {
          // Create request body outside the promise
          const requestBody = {
            orderDateFrom: dayjs(orderDateFrom).format("YYYY-MM-DD"),
            orderDateTo: dayjs(orderDateTo).format("YYYY-MM-DD"),
            packId: pin ? pin.split(",").map((num) => Number(num.trim())) : [],
            itemId: min ? min.split(",").map((num) => Number(num.trim())) : [],
            shipFromLocationId: depot
              ? depot.split(",").map((num) => Number(num.trim()))
              : [],
            shipToLocationId: validSearchStores,
            orderStatus: ORDER_STATUS,
            reasonCode: reason,
            userType: "CENTRAL_SUPPLYCHAIN",
          };

          // Create a new scope for the current offset
          const currentOffset = offset;

          await new Promise((resolve) => {
            dispatch(
              approvalManualOrders({
                reqBody: requestBody,
                apiKey: SOMS_API_KEY,
                limit,
                offset: currentOffset,
              })
            ).then(() => resolve());
          });

          offset += limit;
        }
      }
      setIsFetchingAllOrders(false);
    }
  };

  const formatDates = (orders) => {
    return orders.map((order) => {
      const formattedOrder = { ...order };
      if (formattedOrder.orderDate) {
        formattedOrder.orderDate = dayjs(formattedOrder.orderDate).format(
          "YYYY-MM-DDTHH:mm:ss"
        );
      }
      return formattedOrder;
    });
  };

  const handleForApprove = () => {
    const rowsToApprove = orders.filter((item) =>
      selectedRows.includes(item.manualOrderId)
    );

    rowsToApprove.map((row) => {
      return { ...row, status: "approved" };
    });

    const formattedData = formatDates(rowsToApprove);
    setApprovedOrders(formattedData);
    dispatch(
      processOrders({ orders: formattedData, action: ORDER_ACTIONS.APPROVE })
    );
    isProcessOrder.current = false;
  };

  const handleDownloadAndReadFile = () => {
    const width = 500;
    const height = 300;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const newWindow = window.open(
      "",
      "_blank",
      `width=${width},height=${height},left=${left},top=${top}`
    );
    if (newWindow) {
      const container = newWindow.document.createElement("div");
      newWindow.document.body.appendChild(container);
      const root = ReactDOM.createRoot(container);
      root.render(
        <Provider store={reduxStore}>
          <PersistGate loading={null} persistor={persistor}>
            <ReasonCodesPopup />
          </PersistGate>
        </Provider>
      );
      newWindow.document.close();
    }
  };

  const disableForOnlyPastDate = (date) => {
    const today = dayjs();
    return date.isBefore(today, "day");
  };

  const disableOrderDateTo = (date) => {
    const fromDate = dayjs(orderDateFrom);
    const toDate = fromDate.add(4, "day");
    const currentDate = dayjs(date);

    return (
      currentDate.isBefore(fromDate, "day") ||
      currentDate.isAfter(toDate, "day")
    );
  };

  const isNumericWithCommas = (value) => {
    return value.split(",").every((v) => /^[0-9, \s]*$/.test(v.trim()));
  };

  const updateValidationErrors = (field, message) => {
    setValidationErrors((previous) => ({
      ...previous,
      [field]: {
        error: true,
        message: message,
      },
    }));
  };

  const handleSearch = () => {
    dispatch({ type: RESET_APPROVAL_ORDER });
    const errors = [];
    const validSearchStores = [];
    setValidationErrors({});

    if (errors.length > 0) {
      setMandatoryFieldsError(errors);
      return;
    } else {
      if (store) {
        const storeArr = store.split(",").map((x) => parseInt(x));
        const invalidStores = [];
        storeArr.forEach(function (store) {
          if (validStores.indexOf(store) < 0) {
            invalidStores.push(store);
          } else {
            validSearchStores.push(store);
          }
        });
        if (invalidStores.length > 0) {
          errors.push(
            `The following store(s) are not valid: ${invalidStores.join(", ")}`
          );
        }
      }
      setMandatoryFieldsError([]);
    }

    if (!store || validSearchStores.length > 0) {
      const updatedOffset = 0;
      setOffset(updatedOffset);
      fetchOrders(validSearchStores, updatedOffset);
    }
  };

  const handleReasonChange = (event) => {
    setReason(event.target.value); // Update the state when a menu item is selected
  };

  const ReasonCell = ({ reasonCode }) => {
    let description = "";
    reasonCodes?.forEach((reason) => {
      if (reason.reasonCode === reasonCode) {
        description = reason.description;
      }
    });
    return <>{description}</>;
  };

  return (
    <div className="App mainPageStyling">
      <div style={{ display: "flex", alignItems: "center" }}>
        {isFetchingAllOrders && (
          <Backdrop
            sx={{
              color: "#fff",
              flexDirection: "column",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={true}
          >
            <CircularProgress color="inherit" />

            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Loading...
            </Typography>
          </Backdrop>
        )}
        <Typography component="h1" variant="h5" sx={{ mb: 2, mr: 2 }}>
          Approve Manual Orders
        </Typography>
        <CutOffTimeAlert actionType={CUTOFF_ACTION.approve_manual_orders} />
      </div>
      <div style={{ marginBottom: "10px" }} className="labelControl">
        <Grid sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <Grid container spacing={3} sx={{ "& > *": { flex: 1 } }}>
            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Order Date From" labelId="orderDateFrom">
                <DatePicker
                  format="DD/MM/YYYY"
                  id="orderDateFrom"
                  variant="outlined"
                  size="small"
                  value={orderDateFrom}
                  shouldDisableDate={disableForOnlyPastDate}
                  onChange={(newValue) => {
                    setOrderDateFrom(newValue);
                    setOrderDateTo(dayjs(newValue).add(4, "day"));
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      placeholder: "Order Date From",
                      helperText: validationErrors?.orderDateFrom?.message,
                      error: validationErrors?.orderDateFrom?.error,
                      inputProps: { 'data-testid': 'order-date-from-input' },
                    },
                  }}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Order DateTo " labelId="orderDateTo">
                <DatePicker
                  format="DD/MM/YYYY"
                  id="orderDateTo"
                  variant="outlined"
                  size="small"
                  value={orderDateTo}
                  minValue={orderDateFrom}
                  shouldDisableDate={disableOrderDateTo}
                  onChange={(newValue) => setOrderDateTo(newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      placeholder: "Order Date To",
                      helperText: validationErrors?.orderDateTo?.message,
                      error: validationErrors?.orderDateTo?.error,
                      inputProps: { 'data-testid': 'order-date-to-input' },
                    },
                  }}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3.5}>
              <LabelWithControl
                label={<>MIN &nbsp;or&nbsp; PIN</>}
              ></LabelWithControl>
              <TextField
                id="min"
                variant="outlined"
                size="small"
                placeholder="Min"
                value={min}
                style={{ width: "49%", float: "left" }}
                onChange={(e) => {
                  setMin(e.target.value);
                  if (e.target.value) {
                    setPin("");
                  }
                }}
                disabled={pin.length > 0}
                inputProps={{
                  style: {
                    background: pin.length > 0 ? "#f5f5f5" : "inherit",
                  },
                }}
                helperText={validationErrors?.min?.message}
                error={validationErrors?.min?.error}
              />
              <TextField
                id="pin"
                variant="outlined"
                size="small"
                placeholder="Pin"
                value={pin}
                style={{ width: "50%", float: "left", paddingLeft: "24px" }}
                onChange={(e) => {
                  setPin(e.target.value);
                  if (e.target.value) {
                    setMin("");
                  }
                }}
                disabled={min.length > 0}
                inputProps={{
                  style: {
                    background: min.length > 0 ? "#f5f5f5" : "inherit",
                  },
                }}
                helperText={validationErrors?.pin?.message}
                error={validationErrors?.pin?.error}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <LabelWithControl label="Depot ID" labelId="depotId">
                <TextField
                  id="depotId"
                  variant="outlined"
                  size="small"
                  placeholder="Depot"
                  value={depot}
                  onChange={(e) => setDepot(e.target.value)}
                  helperText={validationErrors?.depot?.message}
                  error={validationErrors?.depot?.error}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <LabelWithControl label="Store ID" labelId="storeId">
                <TextField
                  id="storeId"
                  variant="outlined"
                  size="small"
                  placeholder="Store"
                  value={store}
                  onChange={(e) => setStore(e.target.value)}
                  helperText={validationErrors?.store?.message}
                  error={validationErrors?.store?.error}
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Reason">
                <Select
                  value={reason}
                  placeholder="Reason"
                  onChange={handleReasonChange}
                  sx={{ height: "35px" }}
                  className="dropDownValue"
                >
                  <MenuItem value="" disabled key={0}>
                    {" "}
                    Select Reason{" "}
                  </MenuItem>
                  {reasonCodes &&
                    reasonCodes.map((reason) => (
                      <MenuItem
                        value={reason.reasonCode}
                        key={reason.reasonCodeId}
                      >
                        {reason.description}
                      </MenuItem>
                    ))}
                </Select>
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  width: "20px",
                  height: "25px",
                  fontSize: "12px",
                  padding: "3px",
                  marginTop: "16px",
                }}
                onClick={handleSearch}
                fullWidth
              >
                Search
              </Button>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={1}
              style={{
                display: "flex",
                justifyContent: "center",
                justifyItems: "end",
                marginTop: "20px",
              }}
            >
              <Link
                style={{
                  color: "#108810",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
                onClick={handleDownloadAndReadFile}
              >
                View Reason
              </Link>
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          {mandatoryFieldsError.length !== "" && (
            <div className="error-display">
              {mandatoryFieldsError.map((error, idx) => (
                <p key={idx} style={{ color: "red" }}>
                  {error}
                </p>
              ))}
            </div>
          )}
        </Grid>
      </div>

      <>
        <TableContainer
          component={Paper}
          className="tableContainerScroll AmendWrapper tableStructure tableStyle tableStyleApprove"
          ref={tableContainerRef}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Checkbox
                    indeterminate={
                      selectedRows.length > 0 &&
                      selectedRows.length < orders.length
                    }
                    checked={
                      selectedRows.length > 0 &&
                      selectedRows.length === orders.length
                    }
                    onChange={handleSelectAll}
                    className="checkboxStyle"
                  />
                </TableCell>
                <TableCell>MIN</TableCell>
                <TableCell>PIN</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Depot</TableCell>
                <TableCell>Store</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Quantity Override Reason</TableCell>
                <TableCell>Created By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders &&
                orders.length > 0 &&
                orders.map((order) => (
                  <TableRow key={order.manualOrderId}>
                    <TableCell>
                      <Checkbox
                        checked={selectedRows.includes(order.manualOrderId)}
                        onChange={() =>
                          handleCheckboxChange(order.manualOrderId)
                        }
                        className="checkboxStyle"
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      {order.itemId}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      {order.packId}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "flex-start",
                      }}
                    >
                      <Tooltip title={order.itemDescription} arrow>
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                            maxWidth: "480px",
                            verticalAlign: "middle",
                          }}
                        >
                          {order.itemDescription}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      {order.shipFromLocationId}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      {order.shipToLocationId}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      {new Date(order.orderDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      {order.quantityOrdered}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      <ReasonCell reasonCode={order.reasonCode} />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      {order.updatedBy}
                    </TableCell>
                  </TableRow>
                ))}
              {isviewManualOrdersLoading && (
                <TableRow>
                  <TableCell
                    colSpan={14}
                    style={{
                      textAlign: "center",
                      position: "relative",
                      height: "100px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 12,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!isviewManualOrdersLoading &&
                (!orders ||
                  errorDetails ||
                  (orders && orders.length === 0)) && (
                  <TableRow>
                    <TableCell colSpan={14} style={{ textAlign: "center" }}>
                      {errorDetails && <>{errorDetails.errorMessage}</>}
                      {!errorDetails && (
                        <>
                          No matching records found! Please check your search
                          criteria.
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </TableContainer>
      </>

      {orders && (
        <Grid
          container
          spacing={2}
          sx={{ marginTop: "10px", marginBottom: "20px" }}
          className="paddingTop0 commonButtons marginBottom0"
        >
          <Grid item xs={12} sm={6} md={11} className="innerPadding">
            <Button
              variant="contained"
              color="primary"
              onClick={handleForApprove}
              disabled={selectedRows.length === 0}
            >
              Approve
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={1} className="innerPadding">
            <Button
              variant="contained"
              sx={{
                "&:hover": { backgroundColor: "#D3D3D3" },
                color: "black",
                backgroundColor: "#D3D3D3",
                textTransform: "none",
              }}
              onClick={() => navigate(PAGE.HOME.url)}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      )}
    </div>
  );
};
